import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ShieldX } from 'lucide-react'
import { useAccess, type AccessGates, type AccessInfo } from './use-access'
import { isAccessAllowed } from './access-rule'
import { homeRouteLabel } from './permissions'

interface RequireAccessProps {
  /** Which access gate must be true (e.g. 'canAccessWorkspace'). */
  gate?: keyof AccessGates
  /**
   * Fine-grained predicate for routes whose entry control is guarded by a
   * compound or stricter condition than a single named gate — e.g. the
   * dispatch flow needs `chart_queue` edit, engine-admin Costs needs
   * `system_admin` edit:all. Receives the full access info so it can combine
   * gates and `.can(module, action, scope)` checks. Use this to keep the
   * route guard identical to the button/tab/link that leads here.
   *
   * Exactly one of `gate` or `when` should be provided; if both are given,
   * `when` takes precedence.
   */
  when?: (access: AccessInfo) => boolean
  children: ReactNode
}

/**
 * Route guard — renders children if the access check is satisfied, otherwise
 * shows an inline 403 screen within the AppShell. This is the route-level
 * counterpart to the UI-level gating (hidden/disabled buttons via
 * `ActionButton` and `{gate && ...}`): a page must enforce the SAME permission
 * as the control that navigates to it, so direct-URL access can't bypass a
 * disabled/hidden button.
 */
export function RequireAccess({ gate, when, children }: RequireAccessProps) {
  const access = useAccess()

  const allowed = isAccessAllowed(access, { gate, when })

  if (allowed) {
    return <>{children}</>
  }

  const label = homeRouteLabel(access.homeRoute)

  return (
    <div
      data-access-denied=""
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-20) 0',
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 'var(--r-full)',
          background: 'rgba(198, 25, 64, 0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ShieldX size={28} style={{ color: 'var(--danger)' }} />
      </div>
      <p style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>
        This section isn't available for your role
      </p>
      <Link
        to={access.homeRoute}
        className="btn btn-primary btn-sm"
        style={{ marginTop: 'var(--sp-2)' }}
      >
        Take me to {label}
      </Link>
    </div>
  )
}
